/**
 * Settings — and the place the IPTV vocabulary is allowed to live.
 *
 * ## Why this screen exists
 *
 * The top bar had grown to ten links: six for browsing and four for administering the
 * thing. Two of those four said "Kaynaklar" and led to talk of Xtream, M3U, containers and
 * sync — which is the app describing its plumbing to somebody who wanted to watch a film.
 *
 * Requirement 3 asks for extreme simplicity and requirement 16 for no IPTV terminology in
 * normal browsing. Both are the same fix: browsing keeps the six links it needs, and
 * everything that is really configuration moves behind one door. The words are not
 * censored — they are correct, and hidden them would make the settings screen useless —
 * they are just no longer on the way to a film.
 *
 * ## Grouped by who the row is for
 *
 * "Bu evde" is the household — profiles, devices. "Bu cihazda" is this screen's own
 * device. "Gelişmiş" is where the subscription lives, and it says what it is rather than
 * pretending: somebody who ends up there is looking for exactly that.
 */
import { Link } from 'react-router-dom';
import { useSession } from '../store/session';
import { Avatar } from '../components/Avatar';
import { Icon, type IconName } from '../components/Icon';
import { useRealtimeChannel } from '../realtime/RealtimeContext';

/*
 * The four rows were `👥 📱 📺 🛰️` and the chevron was `›`.
 *
 * This screen was missed by the icon sweep, which is easy to see in hindsight: it was
 * written after it. Every reason in `components/Icon.tsx` applies here and one of them
 * applies with force — the satellite emoji is a photographic colour glyph on Windows, a
 * flat outline on Tizen, and absent on some Android builds, where it renders as a tofu box
 * next to the only row in the app that leads to the subscription.
 */
function Row({
  to,
  title,
  detail,
  icon,
}: {
  to: string;
  title: string;
  detail: string;
  icon: IconName;
}) {
  return (
    <li>
      <Link className="settingsrow" to={to}>
        <span className="settingsrow__icon" aria-hidden>
          <Icon name={icon} size={1.35} />
        </span>
        <span className="settingsrow__text">
          <strong>{title}</strong>
          <span className="settingsrow__detail">{detail}</span>
        </span>
        <span className="settingsrow__chevron" aria-hidden>
          <Icon name="chevronRight" size={1.1} />
        </span>
      </Link>
    </li>
  );
}

export function SettingsPage() {
  const { profile, user, logout } = useSession();
  const { presence } = useRealtimeChannel();

  const televisions = presence.filter((d) => d.deviceType === 'tv');
  const others = presence.length > 0 ? presence.length - 1 : 0;

  return (
    <main className="settings">
      <h1>Ayarlar</h1>

      {profile && (
        <div className="settings__who">
          <Avatar profile={profile} size={48} />
          <div>
            <strong>{profile.name}</strong>
            <span className="settings__email">{user?.email}</span>
          </div>
        </div>
      )}

      <h2 className="settings__group">Bu evde</h2>
      <ul className="settings__list">
        <Row
          to="/profiles/settings"
          title="Profiller"
          detail="Diller, çocuk profili, avatar"
          icon="profiles"
        />
        <Row
          to="/devices"
          title="Cihazlar"
          detail={others > 0 ? `${others} cihaz daha bağlı` : 'Bağlı cihazları yönetin'}
          icon="phone"
        />
        {/* Only when there is one to control. A remote for nothing is a dead end, and the
            row would be the most prominent thing on the screen that does not work. */}
        {televisions.length > 0 && (
          <Row
            to="/remote"
            title="Kumanda"
            detail={televisions[0]?.name ?? 'Televizyon açık'}
            icon="tv"
          />
        )}
      </ul>

      <h2 className="settings__group">Gelişmiş</h2>
      <ul className="settings__list">
        {/*
          The one row that names the plumbing, and it does so plainly. "Kaynaklar" alone
          meant nothing to anybody who had not set it up; the detail line is what makes it
          findable by the person actually looking for it.
        */}
        <Row
          to="/sources"
          title="Yayın aboneliği"
          detail="Sağlayıcı bağlantısı ve içerik güncelleme"
          // `live`, the broadcast arcs — not `pair`, which is a screen receiving a signal
          // and belongs to casting. This row is where the broadcast itself comes from.
          icon="live"
        />
      </ul>

      <button type="button" className="settings__logout" onClick={() => void logout()}>
        Çıkış yap
      </button>
    </main>
  );
}

export default SettingsPage;
